// Pure derivations for the `player` page (ARCHITECTURE §4.3 rule 5). They move
// verbatim out of PlayerDetail.tsx (release/1.0 @ 21609c5) so the values
// templates show cannot drift from classic. The controller and the fixtures
// both build the VM through these functions.

import type { SportPack, StatDef } from '@/contexts/SportContext'
import type { TenantDivision } from '@/contexts/LeagueContext'
import { getImageUrl } from '@/services/api'
import type { GalleryImage, Player, PlayerGameLogEntry, Season, Tournament } from '@/services/api/types'
import { getTeamDisplay } from '@/utils/teamDisplay'
import { formatSeasonLabel, nameWithYear } from '@/utils/seasonLabel'
import type { Option, StatColumn } from '../../template/pages'
import type {
  CareerRow, GameLogGroup, GameLogRow, PackStatTile, PlayerBio, PlayerHero, PlayerStatsEmptyReason, StatHigh, StatRanks, StatsScope,
} from './types'
import { fmtDate } from '@/i18n'

/** Inline photo strip: render up to PHOTO_LIMIT; fetch one more to detect "more exist". */
export const PHOTO_LIMIT = 12
export const PHOTO_FETCH = PHOTO_LIMIT + 1

// ── Stat lookups (PlayerDetail.tsx:899-922, verbatim) ──────────────────────

/**
 * Look up a value on the (basketball-shaped) stats response by any stat key.
 * Tries direct hits first (e.g. `ppg`, `games_played`), then `total_<key>`.
 */
export function lookupStatValue(stats: object, key: string): number | string | null {
  const rec = stats as unknown as Record<string, unknown>
  const direct = rec[key]
  if (typeof direct === 'number' || typeof direct === 'string') return direct
  if (key.startsWith('total_')) {
    return null // explicit total_* lookup that missed
  }
  const total = rec[`total_${key}`]
  if (typeof total === 'number' || typeof total === 'string') return total
  return null
}

/** Best-effort: map a derived per-game key to the raw total it averages. */
export function derivedToRawTotalKey(sport: SportPack, derivedKey: string): string | null {
  for (const raw of sport.stats) {
    if (derivedKey.startsWith(raw.key)) return `total_${raw.key}`
  }
  return null
}

// ── Tiles (PlayerDetail.tsx:56-84, :622-679) ───────────────────────────────

/** Secondary keys: basketball pinned (pixel parity); else the pack's next per-game stats. */
export function secondaryStatKeys(sport: SportPack): string[] {
  if (sport.id === 'basketball') {
    return ['steals', 'blocks', 'three_pointers_made']
  }
  const primaryRawKeys = new Set<string>()
  for (const pk of sport.primaryStatKeys) {
    for (const raw of sport.stats) {
      if (pk.startsWith(raw.key)) primaryRawKeys.add(raw.key)
    }
  }
  return sport.stats
    .filter((s) => s.perGameAverage && !primaryRawKeys.has(s.key))
    .slice(0, 3)
    .map((s) => s.key)
}

/**
 * Pack-keyed numbers from the stats endpoints (#474): `totals` for every
 * summed pack stat (JSONB included), `derived` for the pack's per-game and
 * percentage formulas. Absent on older payloads, then today's fields are used.
 */
export interface PackNumbers {
  totals?: Record<string, number> | null
  derived?: Record<string, number | null> | null
}

/** Pick `totals`/`derived` off a stats response (they sit beside `stats`). */
export function packNumbers(response: unknown): PackNumbers {
  const r = (response ?? {}) as { totals?: unknown; derived?: unknown }
  return {
    totals: r.totals && typeof r.totals === 'object' ? (r.totals as Record<string, number>) : null,
    derived: r.derived && typeof r.derived === 'object' ? (r.derived as Record<string, number | null>) : null,
  }
}

const count = (v: unknown) => String(Math.round(Number(v || 0)))
const oneDp = (v: unknown) => Number(v || 0).toFixed(1)

/**
 * A raw stat's season/career total: today's `total_<key>` field when the API
 * has one (so basketball keeps its exact numbers), else the pack `totals`
 * (soccer shots, saves…), else the raw key.
 */
function rawTotal(stats: object, key: string, pack: PackNumbers): number | string | null {
  const legacy = lookupStatValue(stats, `total_${key}`)
  if (legacy !== null) return legacy
  const t = pack.totals?.[key]
  if (typeof t === 'number') return t
  return lookupStatValue(stats, key)
}

/**
 * Primary tiles from `sport.primaryStatKeys`; the first is the headline.
 * Pack-correct in every template, classic included (#434):
 * - labels: a derived stat's label, else the raw stat's (soccer 'points' → 'Goals');
 * - a derived key (per game / %) reads today's field when the API has one
 *   (basketball ppg), else `derived`, at one decimal: '11.3';
 * - a raw stat key (soccer Goals, Assists) is a count: '2'.
 * `total` is the raw total behind a per-game tile when the pack names it.
 */
export function primaryTiles(sport: SportPack, stats: object, pack: PackNumbers = {}): PackStatTile[] {
  return sport.primaryStatKeys.map((key, i) => {
    const derivedDef = sport.derivedStats.find((d) => d.key === key)
    const rawDef = sport.stats.find((s) => s.key === key)
    const def = derivedDef ?? rawDef
    let value: string
    if (derivedDef) {
      const legacy = lookupStatValue(stats, key)
      const d = pack.derived?.[key]
      value = oneDp(legacy !== null ? legacy : typeof d === 'number' ? d : null)
    } else {
      value = count(rawTotal(stats, key, pack))
    }
    const totalKey = derivedDef ? derivedToRawTotalKey(sport, key) : null
    const totalDef = totalKey ? sport.stats.find((s) => `total_${s.key}` === totalKey) : undefined
    return {
      key,
      label: def?.label ?? key.toUpperCase(),
      short: def?.short ?? key.toUpperCase(),
      value,
      emphasis: i === 0,
      total: totalKey && totalDef ? { value: count(rawTotal(stats, totalDef.key, pack)), label: totalDef.label } : null,
    }
  })
}

/** Supporting totals (not including games played, which the VM carries as `gamesPlayed`). */
export function secondaryTiles(sport: SportPack, stats: object, pack: PackNumbers = {}): PackStatTile[] {
  return secondaryStatKeys(sport).map((key) => {
    const def = sport.stats.find((s) => s.key === key)
    return { key, label: def?.label ?? key, short: def?.short ?? key, value: count(rawTotal(stats, key, pack)), total: null }
  })
}

/** The response's `ranks` (#430) → VM ranks; `tied` defaults to false until the API reports it. */
export function toRanks(raw: unknown): StatRanks | null {
  if (!raw || typeof raw !== 'object') return null
  const out: StatRanks = {}
  for (const [k, v] of Object.entries(raw as Record<string, { rank?: unknown; of?: unknown; tied?: unknown }>)) {
    if (v && typeof v.rank === 'number' && typeof v.of === 'number') out[k] = { rank: v.rank, of: v.of, tied: v.tied === true }
  }
  return Object.keys(out).length ? out : null
}

export function gamesPlayed(stats: { games_played?: number | string | null } | null): number {
  return Number(stats?.games_played || 0)
}

// ── Stats panel state (PlayerDetail.tsx:600-697) ───────────────────────────

export function statsState(
  scope: StatsScope,
  seasonId: number | null,
  competitionId: number | null,
  loading: boolean,
  hasStats: boolean,
): { status: 'idle' | 'loading' | 'ready' | 'empty'; emptyReason: PlayerStatsEmptyReason | null } {
  if (loading) return { status: 'loading', emptyReason: null }
  if (hasStats) return { status: 'ready', emptyReason: null }
  if (scope === 'lifetime') return { status: 'empty', emptyReason: 'lifetime' }
  if (scope === 'season') return seasonId ? { status: 'empty', emptyReason: 'seasonEmpty' } : { status: 'idle', emptyReason: 'pickSeason' }
  return competitionId ? { status: 'empty', emptyReason: 'competitionEmpty' } : { status: 'idle', emptyReason: 'pickCompetition' }
}

/**
 * #720: the entity's divisions, for its season selector (2+ live divisions).
 * `ids`: the live divisions the team or player is in; `divisionOf`: a season's
 * division (the row's own field, else the tenant payload's).
 */
export interface EntitySeasonScope {
  live: readonly TenantDivision[]
  ids: readonly number[]
  divisionOf: (s: Season) => number | null
}

/**
 * The season selector. Without divisions (`scope` absent): every season, as
 * today. #720: with 2+ live divisions, the seasons of the entity's own
 * divisions come first (admin order, API order within each), and a live
 * division's season is labelled with the division ("Men's · Fall 2026"), so
 * four "Fall 2026"s can be told apart. Every other season follows, in API
 * order: `ids` only knows the divisions whose PUBLIC season has the entity, so
 * a past team, a transfer's old division, a no-division season or an archived
 * division's season (UX §1: archived seasons stay in team and player history)
 * must not drop out of the entity's history.
 */
export function seasonOptions(seasons: Season[], scope?: EntitySeasonScope | null): Option<number | null>[] {
  if (!scope) return seasons.map((s) => ({ value: s.id, label: formatSeasonLabel(s) }))
  const label = (s: Season) => {
    const d = scope.live.find((x) => x.id === scope.divisionOf(s))
    return d ? `${d.name} · ${s.name?.trim() ? nameWithYear(s.name.trim(), s.year) : formatSeasonLabel(s)}` : formatSeasonLabel(s)
  }
  const mine = scope.live.filter((d) => scope.ids.includes(d.id))
  const first = mine.flatMap((d) => seasons.filter((s) => scope.divisionOf(s) === d.id))
  const firstIds = new Set(first.map((s) => s.id))
  return [...first, ...seasons.filter((s) => !firstIds.has(s.id))]
    .map((s) => ({ value: s.id as number | null, label: label(s) }))
}

/** Competitions narrowed to the selected season (all when none). */
export function filterCompetitions(competitions: Tournament[], seasonId: number | null | undefined): Tournament[] {
  return seasonId ? competitions.filter((t) => t.season_id === seasonId) : competitions
}

export function competitionOptions(competitions: Tournament[], seasonId: number | null | undefined): Option<number | null>[] {
  return filterCompetitions(competitions, seasonId).map((t) => ({ value: t.id, label: t.name }))
}

// ── Hero and bio (PlayerDetail.tsx:222-232, :324-478) ──────────────────────

export function formatHeight(inches: number | null | undefined): string | null {
  if (!inches) return null
  const feet = Math.floor(inches / 12)
  const remainingInches = inches % 12
  return `${feet}'${remainingInches}"`
}

export function formatWeight(lbs: number | null | undefined): string | null {
  if (!lbs) return null
  return `${lbs} lbs`
}

export function playerHero(player: Player): PlayerHero {
  const team = player.team_name ? { name: player.team_name, short_code: player.team_short_code } : null
  return {
    photoUrl: getImageUrl(player.photo_url),
    silhouetteUrl: '/default-player.svg',
    number: player.jersey_number ?? null,
    firstName: player.first_name,
    lastName: player.last_name,
    fullName: `${player.first_name} ${player.last_name}`,
    nickname: player.nickname || null,
    position: player.position || null,
    teamShort: team ? getTeamDisplay(team, 'short') : null,
    teamName: team ? getTeamDisplay(team, 'full') : null,
    teamLogoUrl: getImageUrl(player.team_logo_url),
    teamColor: player.team_primary_color ?? null,
    isActive: player.is_active === undefined ? null : Boolean(player.is_active),
  }
}

export function playerBio(player: Player): PlayerBio {
  return {
    height: formatHeight(player.height_inches),
    weight: formatWeight(player.weight_lbs),
    position: player.position || null,
    number: player.jersey_number ?? null,
  }
}

// ── Game log (PlayerDetail.tsx:86-91, :924-964) ────────────────────────────

/** Stats shown in the game log (`gameLog: true` in the pack). */
export function gameLogStats(sport: SportPack): StatDef[] {
  return sport.stats.filter((s) => s.gameLog)
}

export function gameLogColumns(sport: SportPack): StatColumn[] {
  return gameLogStats(sport).map((s) => ({ key: s.key, label: s.label, short: s.short, align: 'center' }))
}

export function groupKey(g: PlayerGameLogEntry): string {
  return g.tournament_id ? `t-${g.tournament_id}` : `s-${g.season_id}`
}

/** Competition name, else season name with year; null = classic's 'Other'. */
export function groupTitle(g: PlayerGameLogEntry): string | null {
  if (g.tournament_name) return g.tournament_name
  if (g.season_name) return nameWithYear(g.season_name, g.season_year)
  return null
}

/** Today's short local date; null when undated. */
export function formatLogDate(d: string | null): string | null {
  if (!d) return null
  try {
    return fmtDate(d, { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return null
  }
}

/** The game from the player's team's point of view (classic buildResult, minus colours). */
export function buildResult(g: PlayerGameLogEntry) {
  const isHome = g.player_team_id === g.home_team_id
  const teamScore = isHome ? g.home_score : g.away_score
  const oppScore = isHome ? g.away_score : g.home_score
  const win = teamScore > oppScore
  const tied = teamScore === oppScore
  return {
    isHome,
    opponentId: isHome ? g.away_team_id : g.home_team_id,
    opponentName: isHome ? g.away_team_name : g.home_team_name,
    opponentCode: isHome ? g.away_team_code : g.home_team_code,
    opponentLogo: isHome ? g.away_team_logo : g.home_team_logo,
    teamScore,
    oppScore,
    resultLetter: (tied ? 'T' : win ? 'W' : 'L') as 'W' | 'L' | 'T',
  }
}

export function gameLogRows(entries: PlayerGameLogEntry[], columns: StatColumn[]): GameLogRow[] {
  return entries.map((g, i) => {
    const r = buildResult(g)
    const rec = g as unknown as Record<string, number | null | undefined>
    const prev = i > 0 ? entries[i - 1] : null
    return {
      gameId: g.game_id,
      href: `/game/${g.game_id}`,
      date: g.game_date,
      dateLabel: formatLogDate(g.game_date),
      status: g.status,
      isHome: r.isHome,
      opponent: {
        id: r.opponentId,
        name: r.opponentName,
        code: r.opponentCode,
        logoUrl: getImageUrl(r.opponentLogo),
        href: r.opponentId != null ? `/teams/${r.opponentId}` : null,
      },
      teamScore: r.teamScore,
      opponentScore: r.oppScore,
      result: r.resultLetter,
      played: g.played !== false,
      stats: Object.fromEntries(columns.map((c) => [c.key, Number(rec[c.key] ?? 0)])),
      groupKey: groupKey(g),
      groupLabel: groupTitle(g),
      startsGroup: !prev || groupKey(prev) !== groupKey(g),
    }
  })
}

/** Consecutive runs of rows sharing a group key (a key can recur, as in classic). */
export function groupRows(rows: GameLogRow[]): GameLogGroup[] {
  const out: GameLogGroup[] = []
  for (const row of rows) {
    if (row.startsGroup || out.length === 0) out.push({ key: row.groupKey, title: row.groupLabel, rows: [] })
    out[out.length - 1].rows.push(row)
  }
  return out
}

/** Classic GameLogSection: hidden while the first load is empty, then null when empty. */
export function gameLogStatus(loading: boolean, count: number): 'loading' | 'ready' | 'empty' {
  if (loading && count === 0) return 'loading'
  return count === 0 ? 'empty' : 'ready'
}

// ── Photos ────────────────────────────────────────────────────────────────

export function visiblePhotos(photos: GalleryImage[]): { items: GalleryImage[]; thumbUrls: string[]; hasMore: boolean } {
  const hasMore = photos.length > PHOTO_LIMIT
  const items = hasMore ? photos.slice(0, PHOTO_LIMIT) : photos
  return { items, thumbUrls: items.map((p) => getImageUrl(p.thumbnail_path) || ''), hasMore }
}

// ── Career table and highs (VM growth for Arena/Huddle; classic ignores them) ──

function careerRow(seasonId: number | null, seasonLabel: string | null, year: number | null, games: PlayerGameLogEntry[], columns: StatColumn[]): CareerRow {
  const played = games.filter((g) => g.played !== false)
  const totals: Record<string, number> = {}
  const averages: Record<string, string> = {}
  for (const c of columns) {
    const sum = played.reduce((acc, g) => acc + Number((g as unknown as Record<string, unknown>)[c.key] ?? 0), 0)
    totals[c.key] = sum
    averages[c.key] = (played.length ? sum / played.length : 0).toFixed(1)
  }
  return { seasonId, seasonLabel, year, gamesPlayed: played.length, totals, averages }
}

const time = (d: string | null) => (d ? Date.parse(d) : 0)

/** Per-season rows (newest first) and a career total, from the full game log. */
export function careerTable(entries: PlayerGameLogEntry[], columns: StatColumn[]): { seasons: CareerRow[]; total: CareerRow | null } {
  if (entries.length === 0) return { seasons: [], total: null }
  const bySeason = new Map<number, PlayerGameLogEntry[]>()
  for (const g of entries) {
    const list = bySeason.get(g.season_id)
    if (list) list.push(g)
    else bySeason.set(g.season_id, [g])
  }
  const latest = new Map([...bySeason].map(([id, games]) => [id, Math.max(...games.map((g) => time(g.game_date)))]))
  const seasons = [...bySeason].map(([id, games]) => {
    const g = games[0]
    return careerRow(id, g.season_name ? nameWithYear(g.season_name, g.season_year) : null, g.season_year ?? null, games, columns)
  })
  seasons.sort((a, b) => (b.year ?? 0) - (a.year ?? 0) || (latest.get(b.seasonId!) ?? 0) - (latest.get(a.seasonId!) ?? 0))
  return { seasons, total: careerRow(null, null, null, entries, columns) }
}

/** Single-game bests per column over played games; ties keep the most recent game. */
export function statHighs(rows: GameLogRow[], columns: StatColumn[]): StatHigh[] {
  const out: StatHigh[] = []
  for (const c of columns) {
    let best: GameLogRow | null = null
    for (const r of rows) {
      if (!r.played) continue
      const v = r.stats[c.key] ?? 0
      if (!best || v > (best.stats[c.key] ?? 0) || (v === (best.stats[c.key] ?? 0) && time(r.date) > time(best.date))) best = r
    }
    if (best && (best.stats[c.key] ?? 0) > 0) {
      out.push({
        key: c.key, label: c.label, short: c.short ?? c.label, value: best.stats[c.key],
        gameId: best.gameId, href: best.href, date: best.date, dateLabel: best.dateLabel, opponent: best.opponent,
      })
    }
  }
  return out
}
